<script lang="ts" setup>
import { CountUp } from 'countup.js'
import type { RecipientGift } from '@consumer/types/serializers'
import { useGift } from '@consumer/composables/gift'
import Giftly from '@consumer/modules/recipientGiftLandingV2/components/Giftly.vue'
import GiftlyLogoWhite from '@consumer/modules/v2/layout/Header/GiftlyLogoWhite.vue'
import newMiscGiftlyLogoIcon from '@consumer/assets/icons/new-misc/giftly-logo.svg?svgicon'

const props = defineProps<{
  gift?: RecipientGift
  amount?: number | string | null
  open?: boolean
  // When true (the showcase's redeem-options beat), the card + amount + sender
  // fade away and the giftly slides up on its own. The recipient flow never
  // sets this.
  collapsed?: boolean
  // When true (the showcase's thank-you finale beat), only the white book
  // (cover + "From" page) fades away — the amount and the giftly card stay,
  // with the card sliding up a touch to make room for the thank-you bubble.
  // The recipient flow never sets this.
  finale?: boolean
  // Overrides the gift-derived sender for the folio inscription (the demo
  // showcase addresses the card generically as "Me").
  senderName?: string
}>()

const { senderName: giftSenderName, tagline } = useGift(computed(() => props.gift))
const senderName = computed(() => props.senderName ?? giftSenderName.value)

// "Suggested places" blurb shown on the folio's bottom flap: the first two
// places (name + city) then "and N other places". Hidden when the gift has no
// places (e.g. a fresh env).
const places = computed(() => props.gift?.places ?? [])
const namedPlaces = computed(() => places.value.filter(place => place.name))
const shownPlaces = computed(() => namedPlaces.value.slice(0, 2))
const otherPlacesCount = computed(() => Math.max(0, namedPlaces.value.length - shownPlaces.value.length))

// The blurb as text/bold segments (the place names and the count are bold), so
// the template stays readable and whitespace stays exact.
interface InfoSegment { text: string; bold?: boolean }
const infoSegments = computed<InfoSegment[]>(() => {
  // Generic gift (no named places): fall back to the card's tagline, e.g.
  // "suggested for use at any place" / "any restaurant".
  if (!shownPlaces.value.length) {
    if (!tagline.value) return []
    return [
      { text: 'This gift is suggested for use at ' },
      { text: tagline.value.toLowerCase(), bold: true },
      { text: '.' },
    ]
  }
  const segments: InfoSegment[] = [{ text: 'This gift is suggested for use at ' }]
  shownPlaces.value.forEach((place, index) => {
    if (index > 0) segments.push({ text: ', ' })
    segments.push({ text: place.name!, bold: true })
  })
  if (otherPlacesCount.value > 0) {
    segments.push(
      { text: ', and ' },
      { text: String(otherPlacesCount.value), bold: true },
      { text: ` other ${otherPlacesCount.value === 1 ? 'place' : 'places'}` },
    )
  }
  segments.push({ text: '.' })
  return segments
})

// CountUp starts at 0 and animates to the real amount once the folio opens.
// Triggered via a watcher on `open` rather than via Vue's reactivity on
// `amount` so that re-opening the folio replays the count from 0.
const amountRef = ref<HTMLDivElement>()
let counter: CountUp | null = null

function startCountUp () {
  if (!amountRef.value) return
  // Resolve the target FRESH each time. `amount` may have been
  // undefined or stale when the counter was first created (slow
  // network, parent prop update). `update(...)` is CountUp's
  // built-in way to retarget without re-instantiating, and combined
  // with `reset()` below the count still starts from 0.
  const target = Number(props.amount ?? 0)
  if (!counter) {
    counter = new CountUp(amountRef.value, target, {
      duration: 1.5,
      prefix: '$',
      startVal: 0,
      useEasing: true,
    })
  }
  else {
    counter.update(target)
  }
  counter.reset()
  counter.start()
}

let countUpTimeout: ReturnType<typeof setTimeout> | null = null
watch(() => props.open, (isOpen) => {
  if (!isOpen) return
  // 0.6s delay ≈ the moment the cover-back face turns toward the viewer
  // (halfway through the 1.2s cover-swing). Matches the reveal fade-in.
  countUpTimeout = setTimeout(startCountUp, 600)
})

onBeforeUnmount(() => {
  if (countUpTimeout) clearTimeout(countUpTimeout)
})
</script>

<template>
  <!--
    Folio renders as a small book: a "page" holding the gift content sits
    underneath a "cover" that displays the giftly wordmark. When `open` flips
    to true the cover swings open around its top edge, revealing the card:
    the amount + "Your Gift" label float above (in the card's color), the
    cover-back carries "From <sender>", and the page shows the giftly card.
  -->
  <div class="folio" :class="{ open, collapsed, finale }">
    <!-- Amount header — floats above the card in the greeting-card color. -->
    <div class="folio-amount">
      <div class="folio-amount-label">Your Gift</div>
      <div ref="amountRef" class="folio-amount-value gift-amount">$0</div>
    </div>

    <!-- Card body (page + cover + giftly) — carries the folio's drop-shadow so
         it sits under the card only; the amount floats above as a sibling and
         stays shadowless. -->
    <div class="folio-body">
      <!-- Interior page — the bottom flap. Once the cover swings open and the
           giftly card slides up to cover the top flap, this shows the "From"
           inscription and the suggested-places blurb below it. -->
      <div class="folio-page">
        <div class="folio-flap-content">
          <div class="folio-from">
            <div class="folio-from-label">From</div>
            <div class="folio-from-name">{{ senderName }}</div>
          </div>

          <div v-if="infoSegments.length" class="folio-info"><span
            v-for="(segment, i) in infoSegments"
            :key="i"
            :class="{ 'folio-info-bold': segment.bold }"
          >{{ segment.text }}</span></div>
        </div>
      </div>

      <!-- Swinging cover. Two faces (front + back) sit back-to-back so the
           user sees the back face once the cover passes 90° of rotation. The
           back is plain white — the top flap the giftly card grows to cover. -->
      <div class="folio-cover">
        <div class="folio-face folio-cover-front">
          <GiftlyLogoWhite :style="{ transform: 'rotateX(15deg)', zIndex: 1 }" :opacity="1"/>
          <GIcon
            :svg="newMiscGiftlyLogoIcon"
            class="absolute folio-logo"
            :style="`
            width: 150%;
            height: 150%;
            top: -242%;
            left: 69%;
            scale: 6;
            transform: rotateX(-25deg);
            z-index: 0;
          `"
          />
        </div>
        <div class="folio-face folio-cover-back"/>
      </div>

      <!-- Giftly card — emerges straddling the fold, then slides up and grows
           to cover the whole top flap (the swung-up cover-back). Brought
           forward (translateZ) so it sits in front of the cover-back. -->
      <Giftly v-if="gift" :gift="gift" class="folio-giftly"/>
    </div>
  </div>
</template>

<style scoped lang="scss">
$folio-radius: 20px;

.folio {
  @media (min-width: 1024px) {
    transform: scale(1.1);
  }

  height: 220px;
  position: relative;
  transform: scale(0.9);
  transform-style: preserve-3d;
  width: 320px;
}

// Wraps the physical card (page + cover + giftly) so the drop-shadow covers the
// card only. The amount is a sibling of this element, so it stays shadowless.
.folio-body {
  filter: drop-shadow(5px 5px 10px rgb(0 0 0 / 30%));
  inset: 0;
  position: absolute;
  transform-style: preserve-3d;
}

// Amount + "Your Gift" label. Sits above the whole open card (above the
// swung-up cover-back), in the envelope color to tie it to the gift's palette.
// Fades in once the folio opens.
.folio-amount {
  bottom: 100%;
  color: var(--env-color);
  left: 0;

  // Clear the cover-back (which swings up ~215px above the folio's top edge)
  // plus a gap, so the amount sits in the card-colored area with breathing
  // room above the card.
  margin-bottom: 280px;
  opacity: 0;
  position: absolute;
  right: 0;
  text-align: center;
  transform: translateY(16px);

  // Rises + fades in ~halfway through the cover swing (0.6s delay).
  transition:
    opacity 0.55s ease 0.6s,
    transform 0.55s cubic-bezier(0.2, 0.7, 0.2, 1) 0.6s;
  z-index: 6;
}

.folio.open .folio-amount {
  opacity: 1;
  transform: translateY(0);
}

.folio-amount-label {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.folio-amount-value {
  font-size: 82px;
  font-weight: 700;
  letter-spacing: -1px;
  line-height: 1;
}

// Interior page — gift content. Sits behind the cover; rendered first in
// source so it's underneath. When the cover swings open this becomes
// visible.
.folio-page {
  align-items: center;
  background: #fff;
  border-radius: 0 0 #{$folio-radius} #{$folio-radius};
  box-shadow:
    inset 0 14px 26px -16px rgb(60 38 20 / 12%),
    0 12px 28px rgb(0 0 0 / 18%);
  display: flex;
  flex-direction: column;
  inset: 0;

  // Bottom flap content sits in the lower portion — the giftly card covers
  // the top.
  justify-content: center;
  padding: 16px 18px 20px;
  position: absolute;
}

// "From" + suggested-places blurb on the bottom flap. Fades in after the
// cover has opened and the giftly card has slid up to cover the top flap.
.folio-flap-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  opacity: 0;
  transition: opacity 0.5s ease 1s;
  width: 100%;
}

.folio.open .folio-flap-content {
  opacity: 1;
}

// Suggested-places blurb — light-grey rounded panel.
.folio-info {
  background: #f4f5f7;
  border-radius: 14px;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.45;
  padding: 12px 14px;
  text-align: center;
}

.folio-info-bold {
  color: #111e36;
  font-weight: 700;
}

// Giftly card — overlays the opened card straddling the fold line. `top` is
// negative so it rises above the folio's mid (fold) line into the cover-back
// half; translateZ brings it in front of the swung-up cover-back (which is
// tilted ~46px toward the viewer) so its upper part isn't occluded.
.folio-giftly {
  left: 50%;
  margin-left: -145px;
  opacity: 0;
  position: absolute;
  top: -95px;
  transform: translateY(16px) translateZ(70px);
  transition:
    opacity 0.6s ease 0.7s,
    transform 0.6s cubic-bezier(0.2, 0.7, 0.2, 1) 0.7s;
  width: 290px;
  z-index: 10;
}

// On open the card slides up and grows to cover the whole top flap (the
// swung-up cover-back), leaving the bottom flap for the From + places blurb.
.folio.open .folio-giftly {
  opacity: 1;
  transform: translateY(-116px) translateZ(70px) scale(1.32);
}

// Redeem-options beat: keep the $amount, hide the From/places page + cover, and
// drop the giftly card lower so the redeem-options row sits above it (between
// the amount and the card).
.folio.collapsed .folio-page,
.folio.collapsed .folio-cover {
  opacity: 0;
  transition: opacity 0.45s ease;
}

.folio.collapsed .folio-giftly {
  // Same size as on open (don't shrink), dropped lower to clear the options row.
  transform: translateY(80px) translateZ(70px) scale(1.32);
  transition: transform 0.5s cubic-bezier(0.2, 0.7, 0.2, 1);
}

// On mobile the whole scene is already shifted down further (see the stage's
// --folio-shift), and the options row sits higher (top: 32% vs desktop's 40%),
// so the card needs only a small extra drop here to clear the row.
@media (max-width: 1023px) {
  .folio.collapsed .folio-giftly {
    transform: translateY(70px) translateZ(70px) scale(1.32);
  }
}

// Thank-you finale beat: only the white book (cover + "From" page) fades out;
// the amount stays and the giftly card slides up a touch so the thank-you
// bubble has room at its lower-right.
.folio.finale .folio-page,
.folio.finale .folio-cover {
  opacity: 0;
  transition: opacity 0.45s ease;
}

.folio.finale .folio-giftly {
  // Keep the giftly at its open size (don't shrink) while the thank-you
  // graphics show.
  transform: translateY(-70px) translateZ(70px) scale(1.32);
  transition: transform 0.5s cubic-bezier(0.2, 0.7, 0.2, 1);
}

// In the finale the amount slides to a new position. Override the base amount
// transition (which carries a 0.6s reveal delay) with a delay-free one so the
// drop runs in sync with the scene's shift — applied to BOTH the collapsed and
// finale states because CSS uses the transition of the state being moved TO, so
// each direction (9→10 and 10→9) needs it. Otherwise the amount translates
// twice: the scene shift, then a delayed second nudge.
.folio.collapsed .folio-amount,
.folio.finale .folio-amount {
  transition: transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1);
}

// Beat 9 clears the narration pill via the whole-scene shift (the stage's
// --folio-shift now covers beat 9 too), so the amount needs no extra nudge here
// — it holds its open-state position and rides the scene down with the card.

// Drop the amount a touch in the finale so it sits lower, further from the
// narration (overrides the open-state translateY(0); same specificity, later).
.folio.finale .folio-amount {
  transform: translateY(28px);
}

.folio-logo {
  color: var(--env-color);
}

.folio-from {
  align-items: center;

  // Sits on the always-white folio card, so a fixed dark colour (the card's
  // --font-color/--text-dark can be white, which would vanish here).
  color: #111e36;
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
}

.folio-from-label {
  color: #9aa0ab;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.folio-from-name {
  font-size: 22px;
  font-weight: 700;
}

// Swinging cover. transform-origin: center top is the spine — a horizontal
// hinge along the top edge. On open we rotate X so the bottom edge of the
// cover swings UP and over toward the spine, revealing the page below
// (~168° leaves the cover slightly short of fully flipped so it doesn't
// visually overlap the page from behind).
.folio-cover {
  inset: 0;
  position: absolute;
  transform: rotateX(0deg);
  transform-origin: center top;
  transform-style: preserve-3d;
  z-index: 5;
}

.folio-face {
  backface-visibility: hidden;
  inset: 0;
  overflow: hidden;
  position: absolute;
}

.folio-cover-front {
  align-items: center;
  background: var(--page-bg-color);
  border-radius: 0 0 #{$folio-radius} #{$folio-radius};
  display: flex;
  justify-content: center;
}

// Cover-back is the top half of the opened white card, so it's plain white
// to blend seamlessly with the page below it. "From <sender>" is centered in
// this top section.
.folio-cover-back {
  align-items: start;
  background: #fff;

  // Straight-sided; rounded on the flap's visual top. The face is flipped via
  // rotateX(180deg), so rounding its local *top* corners lands the radius on
  // the opened card's visual top (square where it meets the fold).
  border-radius: #{$folio-radius} #{$folio-radius} 0 0;
  display: flex;
  justify-content: center;
  transform: rotateX(180deg);
}

:deep(.giftly-image-v3) {
  box-shadow: inset 0 0 0 1px rgb(0 0 0 / 25%), 0 16px 43px rgb(0 0 0 / 35%);
}

.folio.open .folio-cover {
  animation: folio-swing 1.2s cubic-bezier(0.62, 0.02, 0.16, 1) forwards;
}

// Brightness curve that follows the swing — the cover darkens as it
// passes edge-on (mid-rotation), then lightens back as it lands at the
// open position. Masks the awkward flat-strip look during slow phases.
.folio.open .folio-cover-front {
  animation: folio-cover-shade 1.2s cubic-bezier(0.62, 0.02, 0.16, 1) forwards;
}

@keyframes folio-cover-shade {
  0% {
    filter: brightness(1);
  }

  55% {
    filter: brightness(0.78);
  }

  100% {
    filter: brightness(0.9);
  }
}

@keyframes folio-swing {
  to {
    transform: rotateX(168deg);
  }
}

</style>
